import type { PostHogInterface } from 'posthog-js'

/** localStorage key marking this browser as maayot-internal. */
const INTERNAL_KEY = 'maayot_internal'

/**
 * Marks a browser as belonging to the team so its traffic stays out of every
 * metric.
 *
 * Internal traffic has to be excluded BEFORE anyone logs in. The signup funnel
 * — and the A/B test measured on it — starts at the first anonymous pageview,
 * long before an @maayot.com address exists, so the project's email-domain
 * filter cannot reach that far up the funnel. The two things usually reached
 * for instead are worse: PostHog exposes no $ip property at flag-evaluation
 * time, and excluding by country would throw away Singapore, which is ~13% of
 * real signups rather than the handful of staff it was taken for.
 *
 * So the marker is declared, not inferred: load any page once with ?internal=1
 * and this browser is tagged for good (?internal=0 hands it back).
 */
export const markInternalUser = (posthog: PostHogInterface) => {
  try {
    const param = new URLSearchParams(window.location.search).get('internal')
    if (param === '1') localStorage.setItem(INTERNAL_KEY, '1')
    if (param === '0') localStorage.removeItem(INTERNAL_KEY)

    if (localStorage.getItem(INTERNAL_KEY) !== '1') return

    // Two different channels, both needed — they do not substitute for each other.
    //
    // A super property is stamped on every event this browser sends, anonymous
    // ones included. That is what the project's "filter internal and test
    // users" setting reads, so it covers insights, dashboards and the
    // experiment in one go.
    posthog.register({ is_internal: true })
    // Person properties instead ride along with the feature-flag request, which
    // super properties never reach. Without this a flag cannot target internal
    // users at all. posthog-js persists them, so from the next page load they
    // are already on the first flags call — on the very load that sets the
    // marker the flags may have resolved a moment earlier, which is why the
    // signup flow also honours ?flow=grouped for forcing an arm outright.
    posthog.setPersonPropertiesForFlags({ is_internal: true })
  } catch {
    /* analytics must never break the app */
  }
}
